/**
 * Edenrobe — Scratch Campaign controller
 * - Draws a scratch layer on <canvas>, erases with pointer/touch via destination-out
 * - Throttled sampling of canvas alpha data to compute % scratched
 * - Auto fades out the remaining scratch layer once threshold is hit
 * - Persists "revealed" state per-browser in localStorage so repeat visits
 *   skip the scratch layer entirely and show the reveal image straight away
 */
(function () {
  function init(rootId, options) {
    var root = document.getElementById(rootId);
    if (!root) return;

    var storageKey = root.dataset.storageKey;
    var threshold = parseFloat(root.dataset.threshold || '50');

    var canvas = root.querySelector('.scratch-campaign__canvas');
    var hint = root.querySelector('.scratch-campaign__hint');
    var cta = root.querySelector('.scratch-campaign__cta');
    var preloader = root.querySelector('.scratch-campaign__preloader');

    var alreadyRevealed = false;
    try {
      alreadyRevealed = window.localStorage.getItem(storageKey) === '1';
    } catch (e) {
      alreadyRevealed = false;
    }

    // Chrome-hiding and cursor sparkle can run immediately — nothing to wait on.
    hideKnownChrome();
    if (options.enableGlitterCursor) {
      initGlitterCursor(root, options.sparkleFrequencyMs || 45);
    }
    if (options.enableCustomCursor) {
      applyCustomCursor(root, options.cursorStarColor || '#FFD447', options.cursorWandColor || '#F5A9C1', options.cursorSize || 44);
    }

    function markReady() {
      root.classList.add('is-ready');
      if (preloader) {
        preloader.classList.add('is-hidden');
        window.setTimeout(function () {
          if (preloader.parentNode) preloader.parentNode.removeChild(preloader);
        }, 400);
      }
    }

    function waitForImages() {
      var imgs = root.querySelectorAll('.scratch-campaign__reveal-img');
      var promises = [];
      imgs.forEach(function (img) {
        if (!img.getAttribute('src')) return;
        if (img.complete && img.naturalWidth > 0) return;
        promises.push(
          new Promise(function (resolve) {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true }); // don't hang forever on a broken image
          })
        );
      });
      return Promise.all(promises);
    }

    function waitForVideos() {
      if (!options.hasVideo) return Promise.resolve();
      var isMobile = window.matchMedia('(max-width: 750px)').matches;
      var activeVideo = isMobile
        ? root.querySelector('.scratch-campaign__reveal-video--mobile')
        : root.querySelector('.scratch-campaign__reveal-video--desktop');
      if (!activeVideo) {
        activeVideo = root.querySelector('.scratch-campaign__reveal-video');
      }
      if (!activeVideo) return Promise.resolve();

      // Only fetch the video that's actually going to be shown — the other
      // stays at preload="none" so it never competes for bandwidth.
      activeVideo.preload = 'auto';

      if (activeVideo.readyState >= 2) return Promise.resolve();
      return new Promise(function (resolve) {
        activeVideo.addEventListener('loadeddata', resolve, { once: true });
        activeVideo.addEventListener('error', resolve, { once: true });
      });
    }

    function playRevealVideoIfAny() {
      if (!options.hasVideo) return;
      var isMobile = window.matchMedia('(max-width: 750px)').matches;
      var video = isMobile
        ? root.querySelector('.scratch-campaign__reveal-video--mobile')
        : root.querySelector('.scratch-campaign__reveal-video--desktop');

      // fall back to whichever exists if the preferred one wasn't set
      if (!video) {
        video = root.querySelector('.scratch-campaign__reveal-video');
      }
      if (!video) return;

      video.classList.add('is-visible');
      if (options.videoReplacesImage) {
        var imgs = root.querySelectorAll('.scratch-campaign__reveal-img');
        imgs.forEach(function (img) { img.style.display = 'none'; });
      }
      video.muted = true; // required for reliable autoplay across browsers
      var playPromise = video.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(function () {
          /* autoplay blocked — user can hit native controls if enabled */
        });
      }
    }

    function showCta() {
      if (cta) cta.classList.add('is-visible');
    }

    if (alreadyRevealed) {
      root.classList.add('is-already-revealed');
      Promise.race([
        Promise.all([waitForImages(), waitForVideos()]),
        new Promise(function (resolve) { window.setTimeout(resolve, 8000); })
      ]).then(function () {
        playRevealVideoIfAny();
        showCta();
        markReady();
      });
      return; // no canvas setup at all — straight to revealed state
    }

    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    var dpr = Math.max(window.devicePixelRatio || 1, 1);
    var isDrawing = false;
    var strokeCount = 0;
    var currentStrokeMoved = false;
    var strokesToReveal = parseInt(options.strokesToReveal, 10) || 3;
    var cleared = false;
    var lastCheck = 0;
    var checkInterval = 180; // ms — throttle expensive getImageData reads
    var brushSize = options.brushSize || 45;
    var hasSizedOnce = false;

    function sizeCanvas() {
      var rect = root.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
      return paintScratchLayer();
    }

    function paintScratchLayer() {
      ctx.globalCompositeOperation = 'source-over';
      // Always fill a solid color synchronously first — this guarantees the
      // reveal image/video is covered instantly, even before an optional
      // texture image (or a slow connection) finishes loading.
      ctx.fillStyle = options.scratchColor || '#C0C0C0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      var isMobile = window.matchMedia('(max-width: 750px)').matches;
      var activeTexture = (isMobile && options.scratchTextureMobile)
        ? options.scratchTextureMobile
        : options.scratchTexture;

      if (!activeTexture) {
        return Promise.resolve();
      }

      return new Promise(function (resolve) {
        var img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = function () {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve();
        };
        img.onerror = resolve; // solid color fallback already drawn — fine
        img.src = activeTexture;
      });
    }

    function getPos(evt) {
      var rect = canvas.getBoundingClientRect();
      var clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
      var clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
      return {
        x: (clientX - rect.left) * dpr,
        y: (clientY - rect.top) * dpr
      };
    }

    function scratchAt(pos) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, brushSize * dpr, 0, Math.PI * 2, false);
      ctx.fill();
    }

    function maybeCheckProgress(now) {
      if (cleared) return;
      if (now - lastCheck < checkInterval) return;
      lastCheck = now;

      var sampleW = 60; // downsample for cheap alpha scan
      var sampleH = 60;
      var data;
      try {
        data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      } catch (e) {
        return; // canvas tainted or unreadable — skip, brush still works visually
      }

      var pixels = data.data;
      var total = pixels.length / 4;
      var transparent = 0;
      var step = Math.max(1, Math.floor(total / (sampleW * sampleH)));
      var sampled = 0;

      for (var i = 3; i < pixels.length; i += 4 * step) {
        sampled++;
        if (pixels[i] < 10) transparent++;
      }

      var percent = sampled ? (transparent / sampled) * 100 : 0;

      if (percent >= threshold) {
        clearRemaining();
      }
    }

    function clearRemaining() {
      cleared = true;
      canvas.classList.add('is-cleared');
      hint.classList.add('is-hidden');
      playRevealVideoIfAny();
      showCta();
      try {
        window.localStorage.setItem(storageKey, '1');
      } catch (e) {
        /* localStorage unavailable — fails gracefully, scratch just replays next visit */
      }
    }

    function handleStart(evt) {
      isDrawing = true;
      currentStrokeMoved = false;
      hint.classList.add('is-hidden');
      scratchAt(getPos(evt));
    }

    function handleMove(evt) {
      if (!isDrawing || cleared) return;
      evt.preventDefault();
      currentStrokeMoved = true;
      scratchAt(getPos(evt));
      maybeCheckProgress(performance.now());
    }

    function handleEnd() {
      if (isDrawing && !cleared) {
        strokeCount++;
        if (strokeCount >= strokesToReveal) {
          clearRemaining();
        }
      }
      isDrawing = false;
      maybeCheckProgress(performance.now());
    }

    canvas.addEventListener('mousedown', handleStart);
    canvas.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);

    canvas.addEventListener('touchstart', handleStart, { passive: true });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd);

    window.addEventListener('resize', function () {
      // Only resize the canvas; don't re-block the UI behind the preloader again.
      sizeCanvas();
    });

    // Gate the reveal: only show the stage (scratch canvas + hidden image/video
    // underneath) once the scratch layer is painted AND the reveal media has
    // actually finished downloading — this is what prevents the raw image from
    // flashing through on a slow connection.
    function timeoutFallback(ms) {
      return new Promise(function (resolve) {
        window.setTimeout(resolve, ms);
      });
    }

    Promise.race([
      Promise.all([sizeCanvas(), waitForImages(), waitForVideos()]),
      timeoutFallback(8000)
    ]).then(function () {
      hasSizedOnce = true;
      markReady();
    });
  }

  /**
   * Directly hides the footer and known chrome elements by ID/selector,
   * regardless of whether the body.campaign-fullscreen class / theme.liquid
   * edit was applied. This is the reliable fallback — it runs after DOM
   * ready and just yanks these elements out of view.
   */
  function hideKnownChrome() {
    var selectors = [
      '#footer',
      'footer',
      '#shopify-section-sections--25532902080785__email_signup_34aPaY',
      '[id*="email_signup"]',
      '[id^="shopify-section-footer"]'
    ];
    selectors.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(function (el) {
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.setAttribute('aria-hidden', 'true');
      });
    });
  }

  /**
   * Replaces the mouse cursor with a hand-drawn star + wand icon rendered on
   * an offscreen <canvas> using vector shapes (no emoji font dependency —
   * this guarantees identical rendering on every OS/browser, unlike relying
   * on a system emoji glyph which can render as a blank "tofu" box).
   */
  function applyCustomCursor(root, starColor, wandColor, size) {
    try {
      var offscreen = document.createElement('canvas');
      offscreen.width = size;
      offscreen.height = size;
      var ctx = offscreen.getContext('2d');

      // Star: standard 5-point star at top-left of the icon. Build the exact
      // outer-point coordinates first so the wand can attach precisely to
      // one of the 5 corners instead of floating nearby.
      var cx = size * 0.32;
      var cy = size * 0.3;
      var outerR = size * 0.26;
      var innerR = outerR * 0.45;
      var outerPoints = [];
      ctx.beginPath();
      for (var i = 0; i < 10; i++) {
        var r = i % 2 === 0 ? outerR : innerR;
        var angle = (Math.PI / 5) * i - Math.PI / 2;
        var x = cx + Math.cos(angle) * r;
        var y = cy + Math.sin(angle) * r;
        if (i % 2 === 0) outerPoints.push({ x: x, y: y });
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = starColor;
      ctx.fill();
      ctx.lineWidth = Math.max(1, size * 0.02);
      ctx.strokeStyle = 'rgba(0,0,0,0.15)';
      ctx.stroke();

      // Wand handle: starts exactly at the star's bottom-right corner
      // (outerPoints[2], the point facing down-right) and runs out to the
      // bottom-right edge of the icon.
      var attachPoint = outerPoints[2];
      ctx.strokeStyle = wandColor;
      ctx.lineWidth = Math.max(2, size * 0.09);
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(attachPoint.x, attachPoint.y);
      ctx.lineTo(size * 0.92, size * 0.94);
      ctx.stroke();

      var dataUrl = offscreen.toDataURL('image/png');
      var hotspot = Math.round(size * 0.15);
      var cursorValue = 'url("' + dataUrl + '") ' + hotspot + ' ' + hotspot + ', auto';

      var style = document.createElement('style');
      style.textContent =
        '#' + root.id + ' { cursor: ' + cursorValue + '; } ' +
        '#' + root.id + ' .scratch-campaign__canvas { cursor: ' + cursorValue + '; } ' +
        '#' + root.id + ' a, #' + root.id + ' button { cursor: ' + cursorValue + '; }';
      document.head.appendChild(style);
    } catch (e) {
      /* cursor image unsupported in this browser — silently keep default cursor */
    }
  }

  /**
   * Sparkle/glitter cursor trail — spawns small twinkling star particles
   * that follow the pointer and fade out, similar to custom-cursor.com's
   * "sparkles and twinkles / glitter" trail. Pure DOM + CSS animation,
   * throttled so it stays smooth on mobile.
   */
  function initGlitterCursor(root, spawnIntervalMs) {
    var layer = document.createElement('div');
    layer.className = 'scratch-campaign__sparkle-layer';
    root.appendChild(layer);

    var lastSpawn = 0;
    var colors = ['#FFD700', '#FFFFFF', '#FF9ED8', '#7FE7FF', '#C9A6FF'];

    function spawnSparkle(x, y) {
      var el = document.createElement('span');
      el.className = 'scratch-campaign__sparkle';
      el.textContent = Math.random() > 0.5 ? '✦' : '✧';

      var size = 10 + Math.random() * 14;
      var drift = (Math.random() - 0.5) * 40;
      var rise = 20 + Math.random() * 30;
      var rotate = (Math.random() - 0.5) * 180;
      var color = colors[Math.floor(Math.random() * colors.length)];

      el.style.left = x + 'px';
      el.style.top = y + 'px';
      el.style.fontSize = size + 'px';
      el.style.color = color;
      el.style.textShadow = '0 0 6px ' + color + ', 0 0 12px ' + color;
      el.style.setProperty('--drift', drift + 'px');
      el.style.setProperty('--rise', '-' + rise + 'px');
      el.style.setProperty('--rotate', rotate + 'deg');

      layer.appendChild(el);
      window.setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, 900);
    }

    function handlePointer(evt) {
      var now = performance.now();
      if (now - lastSpawn < spawnIntervalMs) return;
      lastSpawn = now;

      var point = evt.touches ? evt.touches[0] : evt;
      var rect = root.getBoundingClientRect();
      spawnSparkle(point.clientX - rect.left, point.clientY - rect.top);
    }

    root.addEventListener('mousemove', handlePointer);
    root.addEventListener('touchmove', handlePointer, { passive: true });
  }

  window.EdenrobeScratchCampaign = { init: init };
})();
